import React, { useState, useEffect, useCallback } from "react";
import { Paper, Typography, Grid, Card, CardContent, TextField, Button, CircularProgress, Box, MenuItem, Select, FormControl, InputLabel, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Tooltip, Tabs, Tab, Avatar } from "@material-ui/core";
import { TrendingUp as TrendingUpIcon, People as PeopleIcon, Assessment as AssessmentIcon, GetApp as GetAppIcon, Refresh as RefreshIcon, FilterList as FilterListIcon, MonetizationOn as MonetizationOnIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import { format, subDays, parseISO } from "date-fns";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title as ChartTitle, Tooltip as ChartTooltip, Legend } from "chart.js";
import { Pie, Bar, Line } from "react-chartjs-2";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, PointElement, LineElement, ChartTitle, ChartTooltip, Legend);

var useStyles = makeStyles(function(theme) { return {
  mainPaper: { flex: 1, padding: theme.spacing(3), overflowY: "scroll" },
  summaryCard: { height: "100%", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "#fff" },
  summaryCardGreen: { background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)" },
  summaryCardBlue: { background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" },
  summaryCardOrange: { background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" },
  summaryValue: { fontSize: "2.5rem", fontWeight: "bold", marginTop: theme.spacing(1) },
  summaryLabel: { fontSize: "0.9rem", opacity: 0.9 },
  filterSection: { marginBottom: theme.spacing(3), padding: theme.spacing(2) },
  chartCard: { padding: theme.spacing(2), height: "400px" },
  chipSuccess: { backgroundColor: "#4caf50", color: "#fff" },
  chipWarning: { backgroundColor: "#ff9800", color: "#fff" },
  loadingContainer: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" },
  tagChip: { margin: "2px", fontSize: "0.7rem", height: 20 },
  phoneCell: { fontFamily: "monospace", fontSize: "0.85rem", color: theme.palette.primary.main },
  originBadge: { display: "inline-block", padding: "2px 8px", borderRadius: 12, fontSize: "0.75rem", fontWeight: "bold" },
  statusChip: { fontWeight: "bold", fontSize: "0.75rem" },
}; });

var STATUS_COLORS = { open: "#4CAF50", pending: "#FF9800", closed: "#9E9E9E" };
var STATUS_LABELS = { open: "Aberto", pending: "Pendente", closed: "Fechado" };
var SOURCE_COLORS = { google: "#4285F4", facebook: "#1877F2", instagram: "#E4405F", whatsapp: "#25D366", direto: "#9E9E9E" };



function formatDate(iso) {
  if (!iso) return "-";
  try { return format(parseISO(iso), "dd/MM/yyyy HH:mm"); }
  catch (e) { return "-"; }
}

function ContactOriginReport() {
  var classes = useStyles();
  var tabState = useState(0);
  var tab = tabState[0];
  var setTab = tabState[1];
  var loadState = useState(false);
  var loading = loadState[0];
  var setLoading = loadState[1];
  var lcState = useState(false);
  var loadingContacts = lcState[0];
  var setLoadingContacts = lcState[1];
  var rdState = useState(null);
  var reportData = rdState[0];
  var setReportData = rdState[1];
  var cdState = useState(null);
  var contactsData = cdState[0];
  var setContactsData = cdState[1];
  var sdState = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  var startDate = sdState[0];
  var setStartDate = sdState[1];
  var edState = useState(format(new Date(), "yyyy-MM-dd"));
  var endDate = edState[0];
  var setEndDate = edState[1];
  var ssState = useState("");
  var selectedSource = ssState[0];
  var setSelectedSource = ssState[1];
  var scState = useState("");
  var selectedCampaign = scState[0];
  var setSelectedCampaign = scState[1];
  var pgState = useState(0);
  var page = pgState[0];
  var setPage = pgState[1];
  var rpState = useState(25);
  var rowsPerPage = rpState[0];
  var setRowsPerPage = rpState[1];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  var fetchReport = useCallback(async function() {
    try {
      setLoading(true);
      var response = await api.get("/contact-origin-report", { params: { startDate: startDate, endDate: endDate, utmSource: selectedSource, utmCampaign: selectedCampaign } });
      setReportData(response.data);
    } catch (err) {
      toast.error("Erro ao carregar o relatorio.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedSource, selectedCampaign]);

  var fetchContacts = useCallback(async function(currentPage) {
    try {
      setLoadingContacts(true);
      var response = await api.get("/contact-origin-report/contacts", { params: { startDate: startDate, endDate: endDate, utmSource: selectedSource, utmCampaign: selectedCampaign, page: currentPage + 1, pageSize: rowsPerPage } });
      setContactsData(response.data);
    } catch (err) {
      toast.error("Erro ao carregar os contatos.");
    } finally {
      setLoadingContacts(false);
    }
  }, [startDate, endDate, selectedSource, selectedCampaign, rowsPerPage]);

  useEffect(function() { fetchReport(); }, [fetchReport]);
  useEffect(function() { if (tab === 1) { fetchContacts(page); } }, [tab, page, fetchContacts]);

  function handleFilter() { fetchReport(); if (tab === 1) { setPage(0); fetchContacts(0); } }
  function handleTabChange(e, newValue) { setTab(newValue); }
  function handleChangePage(e, newPage) { setPage(newPage); fetchContacts(newPage); }
  function handleChangeRowsPerPage(e) { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }

  async function handleExportSummary() {
    try {
      var res = await api.get("/contact-origin-report/export", { params: { startDate: startDate, endDate: endDate, utmSource: selectedSource, utmCampaign: selectedCampaign }, responseType: "blob" });
      var url = window.URL.createObjectURL(new Blob([res.data]));
      var link = document.createElement("a"); link.href = url;
      link.setAttribute("download", "relatorio-origens-" + format(new Date(), "yyyy-MM-dd") + ".csv");
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) { toast.error("Erro ao exportar resumo."); }
  }

  async function handleExportContacts() {
    try {
      var res = await api.get("/contact-origin-report/contacts/export", { params: { startDate: startDate, endDate: endDate, utmSource: selectedSource, utmCampaign: selectedCampaign }, responseType: "blob" });
      var url = window.URL.createObjectURL(new Blob([res.data]));
      var link = document.createElement("a"); link.href = url;
      link.setAttribute("download", "contatos-origem-" + format(new Date(), "yyyy-MM-dd") + ".csv");
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) { toast.error("Erro ao exportar lista."); }
  }

  function getSourceColor(source) {
    var s = (source || "").toLowerCase();
    if (s.includes("instagram")) return "#E1306C";
    if (s.includes("facebook") || s.includes("fb")) return "#1877F2";
    if (s.includes("google")) return "#4285F4";
    if (s.includes("tiktok")) return "#000000";
    if (s.includes("whatsapp")) return "#25D366";
    if (s.includes("youtube")) return "#FF0000";
    return "#9ca3af";
  }

  function getPieChartData() {
    if (!reportData || !reportData.bySource || reportData.bySource.length === 0) return null;
    var labels = reportData.bySource.map(i => i.source || "Direto");
    var data = reportData.bySource.map(i => i.count);
    var colors = reportData.bySource.map(i => getSourceColor(i.source));
    return { labels, datasets: [{ data, backgroundColor: colors, borderWidth: 1 }] };
  }

  function getBarChartData() {
    if (!reportData || !reportData.byCampaign || reportData.byCampaign.length === 0) return null;
    var top10 = reportData.byCampaign.slice(0, 10);
    return { labels: top10.map(i => i.campaign), datasets: [{ label: "Contatos", data: top10.map(i => i.count), backgroundColor: "rgba(59,130,246,0.6)" }, { label: "Tickets", data: top10.map(i => i.tickets), backgroundColor: "rgba(16,185,129,0.6)" }] };
  }

  function getLineChartData() {
    if (!reportData || !reportData.timeline) return null;
    return { labels: reportData.timeline.map(i => format(new Date(i.date + "T12:00:00"), "dd/MM")), datasets: [{ label: "Contatos", data: reportData.timeline.map(i => i.count), borderColor: "rgba(102,126,234,1)", backgroundColor: "rgba(102,126,234,0.1)", tension: 0.4, fill: true }] };
  }

  var pieData = getPieChartData();
  var barData = getBarChartData();
  var lineData = getLineChartData();

  return (
    <MainContainer>
      <MainHeader>
        <Title>Relatorio de Origem</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" startIcon={<RefreshIcon />} onClick={handleFilter}>Atualizar</Button>
          {tab === 0 && <Button variant="contained" color="secondary" startIcon={<GetAppIcon />} onClick={handleExportSummary} disabled={!reportData}>Exportar Resumo</Button>}
          {tab === 1 && <Button variant="contained" color="secondary" startIcon={<GetAppIcon />} onClick={handleExportContacts} disabled={!contactsData}>Exportar CSV</Button>}
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper}>
        <Paper className={classes.filterSection}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}><TextField label="Data Inicial" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} fullWidth InputLabelProps={{shrink:true}} /></Grid>
            <Grid item xs={12} sm={2}><TextField label="Data Final" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} fullWidth InputLabelProps={{shrink:true}} /></Grid>
            <Grid item xs={12} sm={3}><TextField label="Origem (utm_source)" placeholder="ex: instagram" value={selectedSource} onChange={e => setSelectedSource(e.target.value)} fullWidth /></Grid>
            <Grid item xs={12} sm={3}><TextField label="Campanha (utm_campaign)" placeholder="ex: natal" value={selectedCampaign} onChange={e => setSelectedCampaign(e.target.value)} fullWidth /></Grid>
            <Grid item xs={12} sm={2}><Button variant="contained" color="primary" fullWidth onClick={handleFilter}>Filtrar</Button></Grid>
          </Grid>
        </Paper>
        <Tabs value={tab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" style={{marginBottom:16}}>
          <Tab label="Dashboard" />
          <Tab label="Contatos" />
        </Tabs>
        {tab === 0 && (loading ? <Box className={classes.loadingContainer}><CircularProgress /></Box> : reportData ? (
          <div>
            <Grid container spacing={3} style={{marginBottom:24}}>
              <Grid item xs={12} sm={6} md={2}><Card className={classes.summaryCard + " " + classes.summaryCardBlue}><CardContent><PeopleIcon style={{fontSize:40}} /><Typography className={classes.summaryLabel}>Total Contatos</Typography><Typography className={classes.summaryValue}>{reportData.summary?.totalContacts || 0}</Typography></CardContent></Card></Grid>
              <Grid item xs={12} sm={6} md={2}><Card className={classes.summaryCard + " " + classes.summaryCardGreen}><CardContent><AssessmentIcon style={{fontSize:40}} /><Typography className={classes.summaryLabel}>Total Tickets</Typography><Typography className={classes.summaryValue}>{reportData.summary?.totalTickets || 0}</Typography></CardContent></Card></Grid>
              <Grid item xs={12} sm={6} md={2}><Card className={classes.summaryCard + " " + classes.summaryCardOrange}><CardContent><TrendingUpIcon style={{fontSize:40}} /><Typography className={classes.summaryLabel}>Cont. Ativos</Typography><Typography className={classes.summaryValue}>{reportData.summary?.activeContacts || 0}</Typography></CardContent></Card></Grid>
              <Grid item xs={12} sm={6} md={2}><Card className={classes.summaryCard}><CardContent><TrendingUpIcon style={{fontSize:40}} /><Typography className={classes.summaryLabel}>Conversão</Typography><Typography className={classes.summaryValue}>{(reportData.summary?.conversionRate || 0).toFixed(1)}%</Typography></CardContent></Card></Grid>
              <Grid item xs={12} sm={12} md={4}><Card className={classes.summaryCard + " " + classes.summaryCardPurple}><CardContent><MonetizationOnIcon style={{fontSize:40}} /><Typography className={classes.summaryLabel}>Receita Gerada (ROI)</Typography><Typography className={classes.summaryValue}>{formatCurrency(reportData.summary?.totalRevenue || 0)}</Typography></CardContent></Card></Grid>
            </Grid>
            <Grid container spacing={3} style={{marginBottom:24}}>
              <Grid item xs={12} md={6}><Paper className={classes.chartCard}><Typography variant="h6">Origem</Typography>{pieData && <Box style={{height:300}}><Pie data={pieData} options={{maintainAspectRatio:false}} /></Box>}</Paper></Grid>
              <Grid item xs={12} md={6}><Paper className={classes.chartCard}><Typography variant="h6">Campanhas</Typography>{barData && <Box style={{height:300}}><Bar data={barData} options={{maintainAspectRatio:false}} /></Box>}</Paper></Grid>
            </Grid>
            <Paper className={classes.chartCard} style={{marginBottom:24}}><Typography variant="h6">Evolução</Typography>{lineData && <Box style={{height:300}}><Line data={lineData} options={{maintainAspectRatio:false}} /></Box>}</Paper>
            <TableContainer component={Paper}><Table size="small"><TableHead><TableRow><TableCell>Origem</TableCell><TableCell align="center">Contatos</TableCell><TableCell align="right">Receita</TableCell></TableRow></TableHead><TableBody>{reportData.bySource?.map(row => <TableRow key={row.source}><TableCell>{row.source || "Direto"}</TableCell><TableCell align="center">{row.count}</TableCell><TableCell align="right">{formatCurrency(row.revenue)}</TableCell></TableRow>)}</TableBody></Table></TableContainer>
          </div>
        ) : (
          <Box className={classes.loadingContainer}><Typography variant="h6" color="textSecondary">Nenhum dado disponivel. Clique em Filtrar.</Typography></Box>
        ))}
        {tab === 1 && (loadingContacts ? (
          <Box className={classes.loadingContainer}><CircularProgress size={60} /></Box>
        ) : contactsData ? (
          <Paper>
            <Box p={2} display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Contatos ({contactsData.total} no total)</Typography>
              <Typography variant="body2" color="textSecondary">Pagina {contactsData.page} de {contactsData.totalPages}</Typography>
            </Box>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  <TableCell><strong>Nome</strong></TableCell>
                  <TableCell><strong>Telefone</strong></TableCell>
                  <TableCell><strong>Tags</strong></TableCell>
                  <TableCell><strong>Atendente</strong></TableCell>
                  <TableCell><strong>Fila</strong></TableCell>
                  <TableCell><strong>Origem</strong></TableCell>
                  <TableCell><strong>Campanha</strong></TableCell>
                  <TableCell><strong>Primeiro Contato</strong></TableCell>
                  <TableCell><strong>Ultimo Ticket</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Tickets</strong></TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {contactsData.rows.map(function(row) { return (
                    <TableRow key={row.contactId} hover>
                      <TableCell><Box display="flex" alignItems="center"><Avatar style={{width:28,height:28,fontSize:"0.75rem",backgroundColor:"#667eea",marginRight:8}}>{row.name ? row.name.charAt(0).toUpperCase() : "?"}</Avatar><Box><Typography variant="body2" style={{fontWeight:500}}>{row.name || "-"}</Typography>{row.email && <Typography variant="caption" color="textSecondary">{row.email}</Typography>}</Box></Box></TableCell>
                      <TableCell><Typography className={classes.phoneCell}>{row.phone || "-"}</Typography></TableCell>
                      <TableCell><Box display="flex" flexWrap="wrap" style={{maxWidth:160}}>{row.tags && row.tags.length > 0 ? row.tags.map(function(tag){return <Chip key={tag.id} label={tag.name} size="small" className={classes.tagChip} style={{backgroundColor:tag.color || "#607D8B",color:"#fff"}} />;}) : <Typography variant="caption" color="textSecondary">-</Typography>}</Box></TableCell>
                      <TableCell><Typography variant="body2">{row.attendant || "-"}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{row.queue || "-"}</Typography></TableCell>
                      <TableCell>{row.utmSource ? <span className={classes.originBadge} style={{backgroundColor:getSourceColor(row.utmSource)+"22",color:getSourceColor(row.utmSource),border:"1px solid "+getSourceColor(row.utmSource)}}>{row.utmSource}</span> : <Typography variant="caption" color="textSecondary">Direto</Typography>}</TableCell>
                      <TableCell><Tooltip title={row.utmCampaign || ""}><Typography variant="caption" style={{maxWidth:120,display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row.utmCampaign || "-"}</Typography></Tooltip></TableCell>
                      <TableCell><Typography variant="caption">{formatDate(row.firstContactDate)}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{formatDate(row.lastTicketCreatedAt)}</Typography></TableCell>
                      <TableCell align="center">{row.lastTicketStatus ? <Chip label={STATUS_LABELS[row.lastTicketStatus] || row.lastTicketStatus} size="small" className={classes.statusChip} style={{backgroundColor:STATUS_COLORS[row.lastTicketStatus] || "#9E9E9E",color:"#fff"}} /> : <Typography variant="caption" color="textSecondary">-</Typography>}</TableCell>
                      <TableCell align="center"><Chip label={row.totalTickets} size="small" color={row.totalTickets > 0 ? "primary" : "default"} /></TableCell>
                    </TableRow>
                  ); })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={contactsData.total} page={page} onChangePage={handleChangePage} rowsPerPage={rowsPerPage} onChangeRowsPerPage={handleChangeRowsPerPage} rowsPerPageOptions={[10,25,50,100]} labelRowsPerPage="Linhas por pagina:" labelDisplayedRows={function(o){return o.from+"-"+o.to+" de "+o.count;}} />
          </Paper>
        ) : (
          <Box className={classes.loadingContainer}><Typography variant="h6" color="textSecondary">Clique em Filtrar para carregar os contatos.</Typography></Box>
        ))}
      </Paper>
    </MainContainer>
  );
}

export default ContactOriginReport;
